import { useEffect } from 'react';

/**
 * Minimal per-page SEO updater.
 * Updates document.title and the canonical meta tags + (optionally) injects
 * a page-specific JSON-LD <script> block. Cleans the JSON-LD block on unmount
 * so different pages never collide.
 *
 * Usage:
 *   <Seo
 *     title="Hire a CNA · MedCare Memphis"
 *     description="..."
 *     canonical="https://medcaretrainingacademy.com/hire-cna"
 *     jsonLd={{ "@context": "https://schema.org", ... }}
 *   />
 */
const ensureMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  return el;
};

const setMetaContent = (selector, attrs, content) => {
  const el = ensureMeta(selector, attrs);
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const Seo = ({ title, description, canonical, ogImage, jsonLd, robots }) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      setMetaContent('meta[name="description"]', { name: 'description' }, description);
      setMetaContent('meta[property="og:description"]', { property: 'og:description' }, description);
      setMetaContent('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    }
    if (title) {
      setMetaContent('meta[property="og:title"]', { property: 'og:title' }, title);
      setMetaContent('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    }
    if (canonical) {
      setLink('canonical', canonical);
      setMetaContent('meta[property="og:url"]', { property: 'og:url' }, canonical);
    }
    if (ogImage) {
      setMetaContent('meta[property="og:image"]', { property: 'og:image' }, ogImage);
      setMetaContent('meta[name="twitter:image"]', { name: 'twitter:image' }, ogImage);
    }
    if (robots) {
      setMetaContent('meta[name="robots"]', { name: 'robots' }, robots);
    }

    // Per-page JSON-LD — tagged so we can remove it on unmount/page-change.
    let scriptEl = null;
    if (jsonLd) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.setAttribute('data-page-jsonld', 'true');
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      if (prevTitle) document.title = prevTitle;
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [title, description, canonical, ogImage, robots, JSON.stringify(jsonLd)]);

  return null;
};

export default Seo;
